<script setup>
import { computed } from 'vue';

import ButtonNext from 'next/button/Button.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ChannelName from 'dashboard/routes/dashboard/settings/inbox/components/ChannelName.vue';

const props = defineProps({
  inboxList: {
    type: Array,
    default: () => [],
  },
  selectedInboxes: {
    type: Array,
    default: () => [],
  },
  updateSelectedInboxes: {
    type: Function,
    default: () => {},
  },
  isWorking: {
    type: Boolean,
    default: false,
  },
  submitButtonText: {
    type: String,
    default: '',
  },
});

const allSelected = computed(() => {
  return (
    props.inboxList.length > 0 &&
    props.selectedInboxes.length === props.inboxList.length
  );
});

const isInboxSelected = inboxId => {
  return props.selectedInboxes.includes(inboxId);
};

const toggleInbox = inboxId => {
  if (isInboxSelected(inboxId)) {
    props.updateSelectedInboxes(
      props.selectedInboxes.filter(id => id !== inboxId)
    );
  } else {
    props.updateSelectedInboxes([...props.selectedInboxes, inboxId]);
  }
};

const toggleAll = () => {
  if (allSelected.value) {
    props.updateSelectedInboxes([]);
  } else {
    props.updateSelectedInboxes(props.inboxList.map(inbox => inbox.id));
  }
};
</script>

<template>
  <div>
    <table
      class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
    >
      <thead>
        <tr class="border-b border-n-weak">
          <td class="w-12 py-3 ltr:pl-4 ltr:pr-3 rtl:pr-4 rtl:pl-3">
            <input
              type="checkbox"
              :checked="allSelected"
              class="m-0"
              @change="toggleAll"
            />
          </td>
          <th
            class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
          >
            {{ $t('WGPT_BOTS.NEW_FLOW.INBOXES.FORM.NAME') }}
          </th>
          <th
            class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
          >
            {{ $t('WGPT_BOTS.NEW_FLOW.INBOXES.FORM.TYPE') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="inbox in inboxList"
          :key="inbox.id"
          class="border-b border-n-weak last:border-b-0 cursor-pointer hover:bg-n-alpha-1"
          @click="toggleInbox(inbox.id)"
        >
          <td class="w-12 py-4 ltr:pl-4 ltr:pr-3 rtl:pr-4 rtl:pl-3">
            <input
              type="checkbox"
              :checked="isInboxSelected(inbox.id)"
              class="m-0 pointer-events-none"
            />
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4 text-body-main text-n-slate-12">
            <span class="flex items-center gap-2">
              <ChannelIcon :inbox="inbox" class="size-4 text-n-slate-11" />
              {{ inbox.name }}
            </span>
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4 text-body-main text-n-slate-11">
            <ChannelName
              :channel-type="inbox.channel_type"
              :medium="inbox.medium"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="flex justify-between items-center w-full gap-2 py-4 mt-2">
      <span class="text-body-main text-n-slate-11">
        {{
          $t('WGPT_BOTS.NEW_FLOW.INBOXES.FORM.SELECTED_COUNT', {
            selected: selectedInboxes.length,
            total: inboxList.length,
          })
        }}
      </span>
      <ButtonNext
        type="submit"
        :label="submitButtonText"
        :disabled="isWorking"
        :is-loading="isWorking"
      />
    </div>
  </div>
</template>
